import { useEffect, useMemo, useRef, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { useRefresh } from "../contexts/RefreshContext";
import { fmtNum, fmtAmount } from "../lib/format";
import { toast } from "sonner";
import { MagnifyingGlass, X, Columns, DownloadSimple, ArrowClockwise, BookmarkSimple, FloppyDisk } from "@phosphor-icons/react";

const NUM_KEYS = new Set(["qty_ordered", "qty_shipped", "qty_open", "price", "open_value", "offset_days"]);
const MONEY_KEYS = new Set(["price", "open_value"]);   // currency-aware grouping + 2dp (per row's currency)
const DATE_KEYS = new Set(["po_date", "deliv_date", "target_date", "cfs_cutoff_date", "must_ship_by", "dis_date"]);
const MONTHS = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 };
const parseDdMmmYy = (s) => {
  if (!s) return null;
  const m = String(s).match(/^(\d{1,2})-([A-Za-z]{3})-(\d{2})$/);
  if (!m) { const t = Date.parse(s); return isNaN(t) ? null : t; }
  return new Date(2000 + parseInt(m[3], 10), MONTHS[m[2].toLowerCase()] ?? 0, parseInt(m[1], 10)).getTime();
};

const otdStyle = (v) => {
  const s = String(v || "");
  if (s.startsWith("Late")) return { color: "#B91C1C", fontWeight: 700 };
  if (s === "Due soon") return { color: "#B45309", fontWeight: 600 };
  if (s === "On track") return { color: "#0F766E", fontWeight: 600 };
  return { color: "var(--omh-text-muted)" };
};

export default function Reports() {
  const [plantOpts, setPlantOpts] = useState([]);
  const { tick } = useRefresh();
  const [customers, setCustomers] = useState([]);
  const [allCols, setAllCols] = useState([]);
  const [selected, setSelected] = useState([]);
  const [rows, setRows] = useState([]);
  const [currencyOpts, setCurrencyOpts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState("");
  const [showCols, setShowCols] = useState(false);
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const colBtnRef = useRef(null);
  const reqSeq = useRef(0);
  const toggleSort = (k) => setSort((s) => (s.key === k ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "asc" }));

  // saved views
  const [views, setViews] = useState([]);
  const [activeView, setActiveView] = useState("");
  const [showViews, setShowViews] = useState(false);
  const viewsRef = useRef(null);

  // filters
  const [plant, setPlant] = useState("");
  const [customer, setCustomer] = useState("");
  const [status, setStatus] = useState("all");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [dateField, setDateField] = useState("due");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [currency, setCurrency] = useState("");
  const [priceBasis, setPriceBasis] = useState("invoice");

  const buildParams = () => {
    const p = { status, date_field: dateField, price_basis: priceBasis };
    if (plant) p.plant = plant;
    if (customer) p.customer = customer;
    if (currency) p.currency = currency;
    if (overdueOnly) p.overdue_only = true;
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  };

  const load = async (force = false) => {
    const my = ++reqSeq.current;          // guard: only the latest request's response is applied
    setLoading(true);
    try {
      // Customer, customer-code and currency are filtered CLIENT-SIDE (see filteredRows), so the
      // server fetch depends only on status / date / price-basis — switching customer or currency is
      // instant and reuses the cache-warmed dataset (no server round-trip per selection).
      // `force` (explicit Refresh click) bypasses the server cache so a just-shipped PO drops off.
      const r = await api.get("/ms/reports/open-orders", { params: {
        status, date_field: dateField, price_basis: priceBasis,
        ...(overdueOnly ? { overdue_only: true } : {}),
        ...(dateFrom ? { date_from: dateFrom } : {}),
        ...(dateTo ? { date_to: dateTo } : {}),
        ...(force ? { refresh: true } : {}),
      } });
      if (my !== reqSeq.current) return;  // a newer request superseded this one → drop stale data
      setRows(r.data.rows || []);
      // dropdown options from the full (unfiltered) dataset
      setPlantOpts([...new Set((r.data.rows || []).map((x) => x.ms_pcode).filter(Boolean))].sort());
      if ((r.data.currencies || []).length) setCurrencyOpts(r.data.currencies);
      if (!allCols.length) {
        setAllCols(r.data.columns || []);
        setSelected(r.data.default_columns || []);
      }
    } catch (e) {
      if (my === reqSeq.current) toast.error(e?.response?.data?.detail || "Failed to load report");
    } finally {
      if (my === reqSeq.current) setLoading(false);
    }
  };

  const loadViews = () => api.get("/ms/reports/views").then((r) => setViews(r.data || [])).catch(() => {});

  const saveView = async () => {
    const name = window.prompt("Save this report as a view — name it:");
    if (!name || !name.trim()) return;
    const config = { columns: selected, sort, filters: { plant, customer, status, overdueOnly, dateField, dateFrom, dateTo, currency, priceBasis, search } };
    try {
      await api.post("/ms/reports/views", { name: name.trim(), config });
      toast.success(`Saved view “${name.trim()}”`);
      setActiveView(name.trim());
      loadViews();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save view");
    }
  };

  const applyView = (v) => {
    const c = v.config || {};
    const f = c.filters || {};
    if (Array.isArray(c.columns) && c.columns.length) setSelected(c.columns);
    if (c.sort) setSort(c.sort);
    setPlant(f.plant || ""); setCustomer(f.customer || ""); setStatus(f.status || "all");
    setOverdueOnly(!!f.overdueOnly); setDateField(f.dateField || "due");
    setDateFrom(f.dateFrom || ""); setDateTo(f.dateTo || ""); setCurrency(f.currency || "");
    setPriceBasis(f.priceBasis || "invoice");
    setSearch(f.search || "");
    setActiveView(v.name);
    setShowViews(false);
  };

  const deleteView = async (v, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete view “${v.name}”?`)) return;
    try {
      await api.delete(`/ms/reports/views/${v.id}`);
      if (activeView === v.name) setActiveView("");
      loadViews();
    } catch { toast.error("Could not delete view"); }
  };

  useEffect(() => {
    api.get("/ms/customers").then((r) => setCustomers(r.data)).catch(() => {});
    loadViews();
  }, []);

  // close dropdowns on outside click
  useEffect(() => {
    const h = (e) => {
      if (viewsRef.current && !viewsRef.current.contains(e.target)) setShowViews(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // reload whenever a filter changes (cached fetch — fast)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status, overdueOnly, dateField, dateFrom, dateTo, priceBasis]);

  // an explicit global Refresh (tick) forces a fresh, cache-bypassing rebuild
  const _tickMounted = useRef(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!_tickMounted.current) { _tickMounted.current = true; return; }
    load(true);
  }, [tick]);

  // close the columns dropdown on outside click
  useEffect(() => {
    const h = (e) => { if (colBtnRef.current && !colBtnRef.current.contains(e.target)) setShowCols(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const toggleCol = (key) => setSelected((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));
  const moveCol = (key, dir) => setSelected((s) => {
    const i = s.indexOf(key); if (i < 0) return s;
    const j = i + dir; if (j < 0 || j >= s.length) return s;
    const n = [...s]; [n[i], n[j]] = [n[j], n[i]]; return n;
  });
  const colLabel = (key) => (allCols.find((c) => c.key === key) || {}).label || key;

  const filteredRows = useMemo(() => {
    const s = search.trim().toLowerCase();
    const cust = customer.trim();
    const pc = plant.trim();
    const cur = currency.trim().toUpperCase();
    return rows.filter((r) => {
      // client-side customer / customer-code / currency (mode) filters — instant, no refetch
      if (cust && String(r.ms_pcode || "") !== cust && String(r.customer_name || "") !== cust) return false;
      if (pc && String(r.ms_pcode || "") !== pc) return false;
      if (cur && String(r.currency_code || "INR").toUpperCase() !== cur) return false;
      if (!s) return true;
      return (
        String(r.ms_custpo || "").toLowerCase().includes(s) ||
        String(r.cust_po2 || "").toLowerCase().includes(s) ||
        String(r.ms_pono || "").toLowerCase().includes(s) ||
        String(r.customer_name || "").toLowerCase().includes(s) ||
        String(r.ms_pcode || "").toLowerCase().includes(s) ||
        String(r.icode || "").toLowerCase().includes(s) ||
        String(r.name || "").toLowerCase().includes(s));
    });
  }, [rows, search, customer, plant, currency]);

  // Totals reflect the SAME rows the table shows (server filters + the client-side search box),
  // so "Open lines", "Total open qty" and "Total open value" always agree with each other.
  const totalsByCcy = useMemo(() => {
    const out = {};
    for (const r of filteredRows) {
      const c = String(r.currency_code || "INR").toUpperCase();
      out[c] = (out[c] || 0) + (Number(r.open_value) || 0);
    }
    return out;
  }, [filteredRows]);

  const sortedRows = useMemo(() => {
    if (!sort.key) return filteredRows;
    const k = sort.key, dir = sort.dir === "asc" ? 1 : -1;
    const val = (r) => {
      const v = r[k];
      if (NUM_KEYS.has(k)) return Number(v) || 0;
      if (DATE_KEYS.has(k)) return parseDdMmmYy(v) ?? -Infinity;
      return String(v ?? "").toLowerCase();
    };
    return [...filteredRows].sort((a, b) => {
      const va = val(a), vb = val(b);
      if (va < vb) return -dir;
      if (va > vb) return dir;
      return 0;
    });
  }, [filteredRows, sort]);

  const clearFilters = () => {
    setPlant(""); setCustomer(""); setStatus("all"); setOverdueOnly(false);
    setDateField("due"); setDateFrom(""); setDateTo(""); setSearch(""); setCurrency("");
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const r = await api.get("/ms/reports/open-orders", {
        params: { ...buildParams(), fmt: "xlsx", columns: selected.join(",") },
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `open_order_report_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel exported");
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const cell = (r, key) => {
    const v = r[key];
    if (key === "otd_status") return <span style={otdStyle(v)}>{v || "—"}</span>;
    if (v == null || v === "") return <span className="muted">—</span>;
    if (MONEY_KEYS.has(key)) return fmtAmount(v, r.currency_code);
    if (NUM_KEYS.has(key)) return fmtNum(v);
    return v;
  };

  return (
    <Layout breadcrumb={["Home", "Insights", "Open Order Report"]}>
      <div className="jde-page">
        <style>{`
          .rp-bar { display:flex; flex-wrap:wrap; align-items:center; gap:8px; margin-bottom:12px;
            background:var(--omh-bg-panel); border:1px solid var(--omh-divider); border-radius:var(--omh-radius-sm); padding:8px 10px; }
          .rp-bar .jde-input, .rp-bar .jde-select { height:30px; font-size:12px; padding:4px 10px; width:auto; }
          .rp-bar .jde-select { padding-right:26px; min-width:118px; max-width:200px; }
          .rp-bar .jde-btn { height:30px; padding:0 10px; font-size:12px; }
          .rp-search { display:flex; align-items:center; gap:6px; flex:1 1 200px; min-width:170px;
            background:#fff; border:1px solid var(--omh-border-strong); border-radius:var(--omh-radius-sm); height:30px; padding:0 10px; }
          .rp-search input { border:0; outline:none; background:transparent; width:100%; font-size:12px; font-family:inherit; }
          .rp-daterange input[type=date]{ width:130px; }
          .rp-toggle { display:inline-flex; align-items:center; gap:6px; font-size:12px; font-weight:600; cursor:pointer; color:var(--omh-text-muted); white-space:nowrap; }
          .rp-divider { width:1px; align-self:stretch; background:var(--omh-divider); margin:0 2px; }
          .rp-colwrap { position:relative; }
          .rp-colmenu { position:absolute; z-index:40; top:34px; right:0; width:300px; max-height:60vh; overflow:auto;
            background:#fff; border:1px solid var(--omh-divider); border-radius:8px; box-shadow:0 10px 30px rgba(15,23,42,.15); padding:8px; }
          .rp-colrow { display:flex; align-items:center; gap:8px; padding:5px 6px; border-radius:6px; font-size:12.5px; }
          .rp-colrow:hover { background:#F8FAFC; }
          .rp-colrow .grip { margin-left:auto; display:flex; gap:2px; }
          .rp-colrow .grip button { border:1px solid var(--omh-divider); background:#fff; border-radius:4px; width:20px; height:20px; cursor:pointer; font-size:10px; line-height:1; color:#475569; }
          .rp-colrow .grip button:disabled { opacity:.3; cursor:default; }
          .rp-pricebasis { display:inline-flex; border:1px solid var(--omh-border-strong); border-radius:var(--omh-radius-sm); overflow:hidden; height:30px; }
          .rp-pricebasis button { border:0; background:#fff; padding:0 10px; font-size:11.5px; font-weight:600; cursor:pointer; color:var(--omh-text-muted); transition:background-color .15s, color .15s; }
          .rp-pricebasis button + button { border-left:1px solid var(--omh-divider); }
          .rp-pricebasis button.active { background:var(--omh-accent, #1E293B); color:#fff; }
        `}</style>

        <div className="rp-bar" data-testid="reports-filterbar">
          <div className="rp-search">
            <MagnifyingGlass size={13} weight="bold" style={{ color: "var(--omh-text-muted)", flex: "0 0 auto" }} />
            <input placeholder="Search PO / customer / item…" value={search}
              onChange={(e) => setSearch(e.target.value)} data-testid="reports-search" />
          </div>
          <select className="jde-select" value={plant} onChange={(e) => setPlant(e.target.value)} data-testid="reports-plant" title="Customer Code">
            <option value="">All customer codes</option>
            {plantOpts.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select className="jde-select" value={customer} onChange={(e) => setCustomer(e.target.value)} data-testid="reports-customer" title="Customer">
            <option value="">All customers</option>
            {customers.map((c, i) => <option key={`${c.ms_pcode || c.name}-${i}`} value={c.ms_pcode || ""}>{`${c.name}${c.ms_pcode ? ` (${c.ms_pcode})` : ""}`}</option>)}
          </select>
          <select className="jde-select" value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="reports-currency" title="Currency (open value can't be mixed across currencies)" style={{ minWidth: 96 }}>
            <option value="">All currencies</option>
            {currencyOpts.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="jde-select" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="reports-status" title="Status">
            <option value="all">All open</option>
            <option value="open">Fully open</option>
            <option value="partially_shipped">Partially shipped</option>
          </select>
          <div className="rp-pricebasis" data-testid="reports-pricebasis" title="Value open lines at the raw PO price, or the customer's invoice (Partyi) price where set — else the PO price.">
            <button className={priceBasis === "po" ? "active" : ""} onClick={() => setPriceBasis("po")} data-testid="reports-pricebasis-po">PO price</button>
            <button className={priceBasis === "invoice" ? "active" : ""} onClick={() => setPriceBasis("invoice")} data-testid="reports-pricebasis-invoice">Invoice price</button>
          </div>
          <div className="rp-divider" />
          <select className="jde-select" value={dateField} onChange={(e) => setDateField(e.target.value)} data-testid="reports-datefield" title="Date field" style={{ minWidth: 110 }}>
            <option value="due">Due date</option>
            <option value="po">PO date</option>
          </select>
          <div className="rp-daterange" style={{ display: "flex", alignItems: "center", gap: 6 }} title="Date range">
            <input type="date" className="jde-input" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="reports-datefrom" />
            <span className="muted" style={{ fontSize: 11 }}>→</span>
            <input type="date" className="jde-input" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="reports-dateto" />
          </div>
          <label className="rp-toggle" data-testid="reports-overdue-toggle">
            <input type="checkbox" checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} data-testid="reports-overdue-checkbox" />
            Overdue only
          </label>
          {(plant || customer || currency || status !== "all" || overdueOnly || dateFrom || dateTo || search) && (
            <button className="jde-btn" onClick={clearFilters} data-testid="reports-clear"><X size={11} weight="bold" /> Clear</button>
          )}
          <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
            <div className="rp-colwrap" ref={viewsRef}>
              <button className="jde-btn" onClick={() => setShowViews((s) => !s)} data-testid="reports-views-btn">
                <BookmarkSimple size={13} weight="bold" /> {activeView ? `View: ${activeView}` : "Views"}
              </button>
              {showViews && (
                <div className="rp-colmenu" style={{ width: 260 }} data-testid="reports-views-menu">
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", padding: "2px 6px 6px" }}>SAVED VIEWS</div>
                  {views.length === 0 && <div className="rp-colrow muted" style={{ cursor: "default" }}>No saved views yet</div>}
                  {views.map((v) => (
                    <div className="rp-colrow" key={v.id} onClick={() => applyView(v)} style={{ cursor: "pointer" }} data-testid={`reports-view-${v.id}`}>
                      <BookmarkSimple size={12} weight={activeView === v.name ? "fill" : "regular"} />
                      <span style={{ fontWeight: activeView === v.name ? 700 : 400 }}>{v.name}</span>
                      <span className="grip"><button title="Delete" onClick={(e) => deleteView(v, e)} data-testid={`reports-view-del-${v.id}`}><X size={9} weight="bold" /></button></span>
                    </div>
                  ))}
                  <div style={{ borderTop: "1px solid var(--omh-divider)", marginTop: 6, paddingTop: 6 }}>
                    <button className="jde-btn jde-btn-primary" style={{ width: "100%", justifyContent: "center" }} onClick={saveView} data-testid="reports-view-save">
                      <FloppyDisk size={12} weight="bold" /> Save current as view…
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="rp-colwrap" ref={colBtnRef}>
              <button className="jde-btn" onClick={() => setShowCols((s) => !s)} data-testid="reports-columns-btn">
                <Columns size={13} weight="bold" /> Columns ({selected.length})
              </button>
              {showCols && (
                <div className="rp-colmenu" data-testid="reports-columns-menu">
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", padding: "2px 6px 6px" }}>REPORT BUILDER — pick &amp; order columns</div>
                  {allCols.map((c) => {
                    const on = selected.includes(c.key);
                    const idx = selected.indexOf(c.key);
                    return (
                      <div className="rp-colrow" key={c.key} data-testid={`reports-col-${c.key}`}>
                        <input type="checkbox" checked={on} onChange={() => toggleCol(c.key)} data-testid={`reports-col-toggle-${c.key}`} />
                        <span>{c.label}</span>
                        {on && (
                          <span className="grip">
                            <button disabled={idx <= 0} onClick={() => moveCol(c.key, -1)} title="Move up" data-testid={`reports-col-up-${c.key}`}>↑</button>
                            <button disabled={idx >= selected.length - 1} onClick={() => moveCol(c.key, 1)} title="Move down" data-testid={`reports-col-down-${c.key}`}>↓</button>
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <button className="jde-btn" onClick={() => load(true)} disabled={loading} title="Refresh (fresh data)" data-testid="reports-refresh"><ArrowClockwise size={13} weight="bold" /></button>
            <button className="jde-btn jde-btn-primary" onClick={exportXlsx} disabled={exporting || !selected.length} data-testid="reports-export">
              <DownloadSimple size={13} weight="bold" /> {exporting ? "Exporting…" : "Export Excel"}
            </button>
          </div>
        </div>

        {/* Summary */}
        <div style={{ background: "white", border: "1px solid var(--jde-border)", padding: "10px 14px", fontSize: 12, marginBottom: 12, display: "flex", gap: 24, fontFamily: "var(--jde-font-mono)", flexWrap: "wrap", alignItems: "center" }} data-testid="reports-summary">
          <div><span className="muted">Open lines:</span> <strong>{fmtNum(filteredRows.length)}</strong></div>
          <div><span className="muted">Total open qty:</span> <strong>{fmtNum(filteredRows.reduce((s, r) => s + (r.qty_open || 0), 0))}</strong></div>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <span className="muted">Total open value:</span>
            {Object.keys(totalsByCcy).length === 0 ? (
              <strong>—</strong>
            ) : (
              Object.entries(totalsByCcy).sort((a, b) => b[1] - a[1]).map(([ccy, val]) => (
                <strong key={ccy} data-testid={`reports-total-${ccy}`}>{fmtAmount(val, ccy)} <span className="muted" style={{ fontSize: 10 }}>{ccy}</span></strong>
              ))
            )}
            <span className="muted" style={{ fontSize: 10 }} data-testid="reports-basis-label">@ {priceBasis === "po" ? "PO price" : "Invoice price"}</span>
            {Object.keys(totalsByCcy).length > 1 && (
              <span className="muted" style={{ fontSize: 10 }}>(pick a currency to export a single-currency sheet)</span>
            )}
          </div>
        </div>

        <div className="jde-panel">
          <div className="jde-panel-body" style={{ padding: 0, overflowX: "auto", opacity: loading ? 0.5 : 1, transition: "opacity .15s" }}>
            <table className="jde-grid" data-testid="reports-table">
              <thead>
                <tr>
                  {selected.map((k) => (
                    <th key={k} onClick={() => toggleSort(k)}
                      style={{ whiteSpace: "nowrap", textAlign: NUM_KEYS.has(k) ? "right" : "left", cursor: "pointer", userSelect: "none", ...(k === "name" ? { maxWidth: 240 } : {}) }}
                      data-testid={`reports-th-${k}`}>
                      {colLabel(k)}{sort.key === k ? <span style={{ marginLeft: 3, opacity: 0.7 }}>{sort.dir === "asc" ? "▲" : "▼"}</span> : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r, i) => (
                  <tr key={i} data-testid={`reports-row-${i}`}>
                    {selected.map((k) => (
                      <td key={k} className={NUM_KEYS.has(k) || String(k).includes("date") || k === "ms_pono" || k === "ms_custpo" ? "text-mono" : undefined}
                        title={k === "name" ? (r.name || "") : undefined}
                        style={{
                          whiteSpace: "nowrap", textAlign: NUM_KEYS.has(k) ? "right" : "left", fontSize: 11.5,
                          ...(k === "name" ? { maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis" } : {}),
                        }}>
                        {cell(r, k)}
                      </td>
                    ))}
                  </tr>
                ))}
                {!loading && filteredRows.length === 0 && (
                  <tr><td colSpan={selected.length || 1} style={{ textAlign: "center", padding: 24 }} className="muted">No open orders match these filters.</td></tr>
                )}
                {loading && (
                  <tr><td colSpan={selected.length || 1} style={{ textAlign: "center", padding: 24 }} className="muted">Loading…</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
